import React from 'react';
import classNames from 'classnames';
import Decimal from 'decimal.js';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import { Tooltip } from 'antd';

import { Link } from 'react-router-dom';
import { by } from '../../utils/sort';
import { formatDateTimeStr, formatNumber } from '../../utils/i18n';
import { TYPES } from '../../constants/gasTaxDeclarations';
import {
  mapGasTaxClass,
  GAS_TAX_CLASSES,
  GAS_TAX_CLASS_NAMES,
  GAS_TAX_CLASSES_2022,
} from '../../constants/types-gastax-classes';
import { UNITS } from '../../constants/units';

const moment = extendMoment(Moment);

const BASE_CLASS = 'tax-declarations';
const TABLE_CLASS = `${BASE_CLASS}__table`;

// Type ("City gate", "Exit zone") reads as one broken word if the column
// ever gets squeezed narrower than its content — force it onto one line so
// the column widens to fit instead of wrapping mid-value.
const NO_WRAP_CELL_CLASS = `${TABLE_CLASS}__cell--nowrap`;

// A history row (one of a metering site's own past declarations, shown as
// its expanded children) only ever carries a date-time in `id` — Name and
// Type never apply to it. Merging the three into one wide cell, instead of
// showing two pointless empty ones, also gives the date-time room to render
// on one line without cramming it into the ID column's own narrow width.
const isHistoryRow = record => !!record.declarationId;

const getIdCellProps = record => (
  isHistoryRow(record)
    ? { colSpan: 3, className: NO_WRAP_CELL_CLASS }
    : { className: NO_WRAP_CELL_CLASS }
);
// Absorbed into the ID cell's colSpan above — must render nothing of its own.
const getMergedCellProps = record => (isHistoryRow(record) ? { colSpan: 0 } : {});
const getTypeCellProps = record => (
  isHistoryRow(record) ? { colSpan: 0 } : { className: NO_WRAP_CELL_CLASS }
);

const getMeteringSiteIdColumn = t => ({
  title: t('common.table.header.id'),
  key: 'id',
  onCell: getIdCellProps,
  render: (record) => {
    const className = classNames(
      {
        [`${TABLE_CLASS}__main-column`]: !!record.meteringSiteId,
      },
    );
    return <span className={className}>{record.id}</span>;
  },
});

const getMeteringSiteNameColumn = t => ({
  title: t('common.table.header.meteringSite'),
  key: 'label',
  align: 'left',
  onCell: getMergedCellProps,
  render: (record) => {
    const className = classNames(
      {
        [`${TABLE_CLASS}__main-column`]: !!record.meteringSiteId,
      },
    );
    const label = record.meteringSiteName;
    return <span className={className}>{label}</span>;
  },
});

const getMeteringSiteTypeColumn = t => ({
  title: t('common.table.header.type'),
  key: 'type',
  align: 'left',
  dataIndex: '',
  onCell: getTypeCellProps,
  render: record => (record.meteringSiteType ? t(`common.label.${record.meteringSiteType}`) : ''),
});

const getRowClassName = record => classNames(
  {
    [`${TABLE_CLASS}__main-row`]: !!record.meteringSiteId,
    [`${TABLE_CLASS}__total-row`]: record.label === 'common.table.header.total',
    [`${TABLE_CLASS}__expanded-row`]: !record.meteringSiteId && record.label !== 'common.table.header.total',
  },
);

// Two-line header: family name, then a code (secondary, e.g. H/R/T) at
// bottom-left and the product-group number (the OmaVero anchor) at
// bottom-right. `code` is '' for the base classes, which just left-pads
// the number over to the right edge.
const renderColumnHeader = (t, key) => (
  <Tooltip title={t(`declareGasTax.table.information.${key}.tooltip`)}>
    <span className={`${TABLE_CLASS}__header`}>
      {t(`declareGasTax.table.information.${key}.row1`)}
      <br />
      <span className={`${TABLE_CLASS}__header-row2`}>
        <span>{t(`declareGasTax.table.information.${key}.code`)}</span>
        <span>{t(`declareGasTax.table.information.${key}.number`)}</span>
      </span>
    </span>
  </Tooltip>
);

// The declare modal's field label. Reuses the table's own number/tooltip
// text (so the two can't drift apart) and renders the number as its own
// fixed-width column (CSS) so it lines up down the form.
const renderTaxClassFieldLabel = (t, key) => (
  <span className={`${BASE_CLASS}__modal-field-label`}>
    <span className={`${BASE_CLASS}__modal-field-label-number`}>
      {t(`declareGasTax.table.information.${key}.number`)}
    </span>
    <span className={`${BASE_CLASS}__modal-field-label-name`}>
      {t(`declareGasTax.table.information.${key}.tooltip`)}
    </span>
  </span>
);

// Plain-text equivalent, for surfaces (e.g. the delete confirmation) that
// show a class name inline rather than in its own layout. table.information
// has an entry for every class, including pg02_natural_gas — not
// declarable, but can still appear in older declarations — so no fallback
// branch is needed here.
const getTaxClassDisplayLabel = (t, key) => (
  `${t(`declareGasTax.table.information.${key}.number`)}. ${t(`declareGasTax.table.information.${key}.tooltip`)}`
);

// Marks a header cell for the vertical-align: bottom rule in index.less
// (keeps the family-name line aligned with the shorter CHP group row).
const HEADER_CELL_CLASS = `${TABLE_CLASS}__header-cell`;
const getHeaderCellProps = () => ({ className: HEADER_CELL_CLASS });

const getConsumptionsColumn = (start, end, resolution, t) => {
  if (!start || !end || !resolution) {
    return [];
  }
  const timestamps = Array
    .from(moment.range(start, end)
      .by(resolution));

  return timestamps.map((time) => {
    const timestamp = time.toISOString();
    return {
      title: renderColumnHeader(t, 'consumption'),
      key: timestamp,
      dataIndex: timestamp,
      align: 'left',
      onHeaderCell: getHeaderCellProps,
    };
  });
};

const isNumericReading = value => (
  value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
);

// Consumption data is already scoped to the requested period (the caller
// fetches exactly [start, end] for the selected month), so no period filter
// is needed here — just find the site and sum its energy readings. Readings
// arrive as numbers or numeric strings (same as elsewhere in this file, e.g.
// flattenEnergyByTimestamp) — non-numeric readings are skipped rather than
// passed to Decimal.add, which would throw on them.
//
// Returns null (not zero) when the site isn't in consumptionsData at all —
// e.g. the consumption fetch hasn't completed yet, such as on a direct/
// bookmarked navigation into the declare modal. Callers must treat null as
// "unknown, don't validate" rather than "zero consumption", or a regular
// customer's declaration gets blocked before their own data has loaded.
const getMeteringSiteConsumptionKwh = (consumptionsData, meteringSiteId) => {
  const site = (consumptionsData || []).find(item => item.meteringSiteId === meteringSiteId);
  if (!site) return null;

  const energyValues = site?.values?.energy || [];
  return energyValues.reduce(
    (total, { value }) => (isNumericReading(value) ? total.add(value) : total),
    new Decimal(0),
  );
};

// Not all classes are needed for declaration form
const getAllowedGasTaxClassNames = () => (GAS_TAX_CLASS_NAMES.filter((taxClassName) => (
  // Base Natural Gas is deducted automatically as the reminder when invoicing
  ![GAS_TAX_CLASSES_2022.PG02_NATURAL_GAS, GAS_TAX_CLASSES.PG02_NATURAL_GAS].includes(taxClassName)
)));


// Grouped under one shared "CHP" header bar (antd/rc-table's nested-
// `children` columns) instead of repeating "CHP" in each column's own header.
const CHP_CLASS_NAMES = [
  GAS_TAX_CLASSES.PG02A_NATURAL_GAS_CHP,
  GAS_TAX_CLASSES.PG08A_BIOGAS_CHP,
  GAS_TAX_CLASSES.PG09A_BIOGAS_CHP_R,
  GAS_TAX_CLASSES.PG10A_BIOGAS_CHP_T,
];

const getTaxClassColumn = (t, name) => ({
  title: renderColumnHeader(t, name),
  key: name,
  dataIndex: name,
  align: 'right',
  onHeaderCell: getHeaderCellProps,
});

const getTaxClassColumns = (t) => {
  const names = getAllowedGasTaxClassNames();
  const chpNames = names.filter(name => CHP_CLASS_NAMES.includes(name));
  const regularColumns = names
    .filter(name => !CHP_CLASS_NAMES.includes(name))
    .map(name => getTaxClassColumn(t, name));

  if (!chpNames.length) {
    return regularColumns;
  }

  return [
    ...regularColumns,
    {
      key: 'chp-group',
      title: <span className={`${TABLE_CLASS}__header-group`}>{t('declareGasTax.table.information.chpGroup')}</span>,
      children: chpNames.map(name => getTaxClassColumn(t, name)),
    },
  ];
};

// "Edit" once the metering site already has a declaration for this period
// (shown as expandable child rows below it), "Declare" while it's still open.
const renderDeclarationLink = (t, meteringSiteId, hasDeclaration) => (
  <Link to={`/declaregastax/${meteringSiteId}`}>
    {t(hasDeclaration ? 'declareGasTax.table.actions.edit' : 'declareGasTax.table.actions.declare')}
  </Link>
);

const renderDeletionLink = (t, declarationId) => (
  <Link to={`/declaregastax/delete/${declarationId}`}>
    {t('declareGasTax.table.actions.delete')}
  </Link>
);

const getDeclarationColumn = () => ({
  title: '',
  key: 'declare',
  dataIndex: 'declare',
  align: 'right',
});

const getRowKey = (item) => {
  const {
    id,
    label,
    declarationId,
    meteringSiteId,
  } = item;

  if (declarationId) {
    return `declaration-${declarationId}`;
  }

  if (meteringSiteId) {
    return `metering-site-${meteringSiteId}`;
  }

  return `${id}-${label || 'row'}`;
};

const flattenEnergyByTimestamp = calculate => (result, {
  timestamp,
  value,
}) => ({
  ...result,
  [timestamp]: value ? formatNumber(calculate(value), 0, 3) : null,
});

const getValue = (item, key, reduceFn, initialValue) => item.values[key].reduce(
  reduceFn,
  initialValue || {
    id: item.meteringSiteId,
    label: `meteringSites.table.information.${key}`,
  },
);

const formatKWhToMWh = (value) => {
  if (typeof (value) !== 'number') return value;
  const multiplier = new Decimal(0.001);
  const calculate = val => Number(multiplier.mul(val));
  return formatNumber(calculate(value), 0, 3);
};

const formatDeclarationTotals = (totals) => {
  const formattedTotals = { ...totals };
  Object.keys(formattedTotals)
    .forEach((key) => {
      formattedTotals[key] = formatKWhToMWh(formattedTotals[key]);
    });
  return formattedTotals;
};

const getDeclarationUpdatedAt = declaration => Date.parse(declaration.createdAt || declaration.timestamp || 0) || 0;

const getLatestDeclarationPerPeriod = declarations => Object.values(
  declarations.reduce((latestDeclarations, declaration) => {
    const periodKey = declaration.period || declaration.timestamp || declaration.id;
    const currentDeclaration = latestDeclarations[periodKey];

    if (!currentDeclaration || getDeclarationUpdatedAt(declaration) >= getDeclarationUpdatedAt(currentDeclaration)) {
      return {
        ...latestDeclarations,
        [periodKey]: declaration,
      };
    }

    return latestDeclarations;
  }, {}),
);

// Returns null (not an empty amounts array) when the point has no
// declaration for this period at all — the modal must tell "nothing to
// prefill from" apart from "the latest declaration for this period was all
// zeroes", same distinction getMeteringSiteConsumptionKwh makes for null
// vs. zero consumption.
const getLatestDeclarationAmounts = (taxDeclarations, meteringPointId, period) => {
  const declarationsForPeriod = (taxDeclarations || []).filter(
    declaration => declaration.meteringPointId === meteringPointId && declaration.period === period,
  );
  const [latestDeclaration] = getLatestDeclarationPerPeriod(declarationsForPeriod);
  if (!latestDeclaration) return null;

  const amountByType = {};
  latestDeclaration.amounts.forEach(({ type, amount }) => {
    amountByType[mapGasTaxClass(type)] = amount;
  });

  return GAS_TAX_CLASS_NAMES.map(taxClassName => ({
    type: taxClassName,
    amount: typeof amountByType[taxClassName] === 'number' ? amountByType[taxClassName] : null,
  }));
};

const getDeclarationAmountTotal = (item, reduceFn, rowObject) => {
  const latestDeclarations = getLatestDeclarationPerPeriod(item.declarations);
  const typeAmounts = latestDeclarations.reduce(
    (accumulator, declaration) => declaration.amounts.reduce(
      reduceFn, accumulator,
    ),
    rowObject,
  );
  const formattedAmounts = formatDeclarationTotals(typeAmounts);
  return formattedAmounts;
};

const flattenDeclarationsByType = () => (result, {
  type,
  amount,
}) => ({
  ...result,
  [type]: result[type] ? result[type] + amount : amount,
});

const getDeclarationRows = (t, item, canDeclare) => {
  const declarationRows = [];
  const declarations = [...item.declarations]
    .sort((a, b) => getDeclarationUpdatedAt(b) - getDeclarationUpdatedAt(a));

  for (const declaration of declarations) {
    const amounts = {};
    for (const amount of declaration.amounts) {
      amounts[amount.type] = formatKWhToMWh(amount.amount);
    }
    const dateTimeStr = formatDateTimeStr(declaration.createdAt);
    const deletionLink = canDeclare ? { declare: renderDeletionLink(t, declaration.id) } : null;
    const rowData = {
      id: dateTimeStr,
      label: 'declaration',
      declarationId: declaration.id,
      ...amounts,
      ...deletionLink,
    };
    declarationRows.push(rowData);
  }
  return declarationRows;
};

const addTotal = (t, data) => {
  const stringToNumber = str => (
    typeof str === 'string'
      ? Number(str.replace(/\s/g, '')
        .replace(',', '.'))
      : 0
  );
  const format = str => formatNumber(str, 0, 3);

  return {
    id: t('common.table.header.total'),
    label: 'common.table.header.total',
    ...data
      .reduce((acc, item) => {
        const {
          id,
          label,
          children,
          ...rest
        } = item;

        Object
          .keys(rest)
          .forEach((key) => {
            // Aggregate all fields except some..
            if (!['declare', 'meteringSiteId', 'meteringSiteName', 'meteringSiteType'].includes(key)) {
              acc[key] = format(
                Decimal.add(
                  stringToNumber(acc[key]),
                  stringToNumber(rest[key]),
                ),
              );
            }
          });
        return acc;
      }, {}),
  };
};

const remap = (t, consumptionsSource, taxDeclarationsSource, unit, canDeclare = false) => {
  const multiplier = new Decimal(unit === UNITS.MWH ? 0.001 : 1);
  const calculate = value => Number(multiplier.mul(value));
  const byType = by({
    key: 'type',
    order: [TYPES.IMATRA, TYPES.EXITZONE, TYPES.BIOGAS, TYPES.CITYGATE],
  });
  const normalizedDeclarations = taxDeclarationsSource.map(declaration => ({
    ...declaration,
    amounts: declaration.amounts.map(amount => ({
      ...amount,
      type: mapGasTaxClass(amount.type),
    })),
  }));
  const mergedData = consumptionsSource.map(item => (
    {
      ...item,
      declarations: normalizedDeclarations.filter(
        declaration => declaration.meteringPointId === item.meteringSiteId,
      ),
    }
  ));
  const remappedData = mergedData
    .sort(byType)
    .map((item) => {
      const energies = getValue(item, 'energy', flattenEnergyByTimestamp(calculate), { id: item.meteringSiteId });
      const declarationAmounts = getDeclarationAmountTotal(
        item,
        flattenDeclarationsByType(),
        { id: item.meteringSiteId },
      );
      const children = getDeclarationRows(t, item, canDeclare);
      const declarationLink = canDeclare ? {
        declare: renderDeclarationLink(t, item.meteringSiteId, item.declarations.length > 0),
      } : null;
      return {
        id: item.id,
        label: item.id,
        meteringSiteId: item.meteringSiteId,
        meteringSiteName: item.name,
        meteringSiteType: item.type,
        ...energies,
        ...declarationAmounts,
        ...declarationLink,
        children: children.length > 0 ? children : undefined,
      };
    });

  return [addTotal(t, remappedData), ...remappedData];
};

export {
  BASE_CLASS,
  TABLE_CLASS,
  getMeteringSiteIdColumn,
  getMeteringSiteNameColumn,
  getMeteringSiteTypeColumn,
  getConsumptionsColumn,
  getTaxClassColumns,
  renderColumnHeader,
  renderTaxClassFieldLabel,
  getTaxClassDisplayLabel,
  getAllowedGasTaxClassNames,
  getMeteringSiteConsumptionKwh,
  getLatestDeclarationAmounts,
  getDeclarationColumn,
  getRowClassName,
  getRowKey,
  remap,
};
